import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { searchMilitaryMOC, getMilitaryMOCSkills, getCareerBySoc } from 'lib/API/careers';
import { BRANCH_TO_API_VALUE } from 'constants/militaryOptions';

// /careers/{soc} and /military-soc/skills expect the compact numeric SOC format (e.g.
// "132011"), unlike the hyphenated/decimal format ("13-2011.00") the military-soc crosswalk returns.
function toCareersSocCode(code) {
  return code ? code.replace('-', '').split('.')[0] : '';
}

export default function MilitaryMOCAutocomplete({
  branch,
  value,
  onChange,
  onSkillsLoadStart,
  onSkillsLoaded,
  error,
  helperText,
  disabled,
  id,
}) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(value?.moc_title || '');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [selectedMOC, setSelectedMOC] = useState(null);
  const [selectedOccupationCode, setSelectedOccupationCode] = useState('');
  const [resolvedSavedTitle, setResolvedSavedTitle] = useState('');
  const debounceRef = useRef(null);

  // Saved entries only carry a soc_code (occupation_id isn't stored with a title) —
  // look the title up once so the field shows a name instead of a bare SOC code.
  useEffect(() => {
    if (selectedMOC || value?.soc_title || !value?.soc_code) {
      setResolvedSavedTitle('');
      return;
    }
    let cancelled = false;
    getCareerBySoc(toCareersSocCode(value.soc_code))
      .then((res) => {
        if (!cancelled) setResolvedSavedTitle(res?.title || '');
      })
      .catch(() => {
        if (!cancelled) setResolvedSavedTitle('');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMOC, value?.soc_title, value?.soc_code]);

  const fetchSkills = useCallback(
    (rawSocCode) => {
      const socCode = toCareersSocCode(rawSocCode);
      if (!socCode) return;
      setSkillsLoading(true);
      if (onSkillsLoadStart) onSkillsLoadStart();
      getMilitaryMOCSkills(socCode)
        .then((skills) => {
          setSkillsLoading(false);
          if (onSkillsLoaded) onSkillsLoaded((skills || []).slice(0, 5));
        })
        .catch(() => {
          setSkillsLoading(false);
          if (onSkillsLoaded) onSkillsLoaded([]);
        });
    },
    [onSkillsLoadStart, onSkillsLoaded]
  );

  const handleInputChange = (_, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      if (selectedMOC && newInputValue !== selectedMOC.title) {
        setSelectedMOC(null);
        setSelectedOccupationCode('');
        onChange(null);
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (newInputValue.length >= 2) {
        debounceRef.current = setTimeout(() => {
          setSearching(true);
          const apiBranch = BRANCH_TO_API_VALUE[branch] || '';
          searchMilitaryMOC(newInputValue, apiBranch)
            .then((data) => {
              const results = Array.isArray(data) ? data : data?.match ?? data?.results ?? [];
              setSearchResults(results);
              setSearching(false);
            })
            .catch(() => {
              setSearchResults([]);
              setSearching(false);
            });
        }, 300);
      } else {
        setSearchResults([]);
      }
    } else if (reason === 'clear') {
      setInputValue('');
      setSearchResults([]);
      setSelectedMOC(null);
      setSelectedOccupationCode('');
      onChange(null);
    }
  };

  const handleMOCSelect = (_, newValue) => {
    if (!newValue || typeof newValue === 'string') {
      setSelectedMOC(null);
      setSelectedOccupationCode('');
      onChange(null);
      return;
    }
    setSelectedMOC(newValue);
    setSelectedOccupationCode('');
    setInputValue(newValue.title);

    const occupations = newValue.occupation || [];
    if (occupations.length === 0) {
      onChange({ moc_code: newValue.code, moc_title: newValue.title, soc_title: '', soc_code: '' });
    } else if (occupations.length === 1) {
      const occ = occupations[0];
      setSelectedOccupationCode(occ.code);
      onChange({ moc_code: newValue.code, moc_title: newValue.title, soc_title: occ.title, soc_code: occ.code });
      fetchSkills(occ.code);
    } else {
      onChange({ moc_code: newValue.code, moc_title: newValue.title, soc_title: '', soc_code: '' });
    }
  };

  const handleOccupationChange = (e) => {
    const code = e.target.value;
    const occ = (selectedMOC?.occupation || []).find((o) => o.code === code);
    if (!occ) return;
    setSelectedOccupationCode(code);
    onChange({ moc_code: selectedMOC.code, moc_title: selectedMOC.title, soc_title: occ.title, soc_code: occ.code });
    fetchSkills(occ.code);
  };

  const occupations = selectedMOC?.occupation || [];
  const showOccupationDropdown = selectedMOC && occupations.length > 1;
  const showAutoSelectedOccupation = selectedMOC && occupations.length === 1;
  // JST-sourced entries only persist occupation_id (soc_code), never a title — fall back
  // to the code so the field still renders instead of silently disappearing.
  const showSavedOccupation = !selectedMOC && Boolean(value?.soc_title || value?.soc_code);
  const savedOccupationLabel = value?.soc_title || resolvedSavedTitle || value?.soc_code || '';

  const OccupationLabel = () => (
    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
      {intl.formatMessage({ id: 'experience.form.selectOccupation' })}
      <Typography component="span" sx={{ color: (theme) => theme.palette.error.main }}>
        *
      </Typography>
    </Typography>
  );

  return (
    <Box>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={searchResults}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={selectedMOC}
        onChange={handleMOCSelect}
        getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.title} (${option.code})`)}
        isOptionEqualToValue={(option, val) => option.code === val?.code}
        loading={searching}
        filterOptions={(x) => x}
        renderOption={(props, option) => {
          const { key, ...rest } = props; // eslint-disable-line no-unused-vars, react/prop-types
          return (
            <li key={option.code} {...rest}>
              {option.title} ({option.code})
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            placeholder={intl.formatMessage({ id: 'experience.form.militaryJobTitlePlaceholder' })}
            error={error}
            helperText={helperText}
            inputProps={{ ...params.inputProps, id }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiInputBase-root:not(.Mui-disabled)': { backgroundColor: 'white' },
            }}
          />
        )}
      />

      {/* Multiple occupations — user picks one */}
      {showOccupationDropdown && (
        <Box sx={{ mt: 1.5 }}>
          <OccupationLabel />
          <FormControl variant="filled" fullWidth>
            <Select
              value={selectedOccupationCode}
              onChange={handleOccupationChange}
              displayEmpty
              disabled={disabled || skillsLoading}
            >
              <MenuItem value="" disabled>
                {intl.formatMessage({ id: 'experience.form.selectOccupationPlaceholder' })}
              </MenuItem>
              {occupations.map((occ) => (
                <MenuItem key={occ.code} value={occ.code}>
                  {occ.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!skillsLoading && selectedOccupationCode === '' && (
            <FormHelperText error>
              {intl.formatMessage({ id: 'experience.form.selectOccupationRequired' })}
            </FormHelperText>
          )}
        </Box>
      )}

      {/* Single occupation — auto-selected, still interactive */}
      {showAutoSelectedOccupation && (
        <Box sx={{ mt: 1.5 }}>
          <OccupationLabel />
          <FormControl variant="filled" fullWidth>
            <Select value={occupations[0].code} disabled={disabled}>
              <MenuItem value={occupations[0].code}>{occupations[0].title}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Saved entry — shows stored title, re-search MOC to change */}
      {showSavedOccupation && (
        <Box sx={{ mt: 1.5 }}>
          <OccupationLabel />
          <FormControl variant="filled" fullWidth>
            <Select value="__saved__" disabled={disabled}>
              <MenuItem value="__saved__">{savedOccupationLabel}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {skillsLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage({ id: 'experience.form.loadingSkills' })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

MilitaryMOCAutocomplete.propTypes = {
  branch: PropTypes.string,
  value: PropTypes.shape({
    moc_code: PropTypes.string,
    moc_title: PropTypes.string,
    soc_title: PropTypes.string,
    soc_code: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  onSkillsLoadStart: PropTypes.func,
  onSkillsLoaded: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};
